<template>
  <SideMenu
    ref="sideMenu"
    :heading="$lang('search', 'title')"
    :side="'right'"
    @on-side-menu-close="$emit('onSearchClose')"
  >
    <div class="pt-24 pb-190 h-full">
      <div class="px-24">
        <TextInput
          class="mb-24 search-input"
          :placeholder="$lang('search', 'placeholder')"
          :has-error="status === LoadingStatuses.ERROR"
          has-search-icon
          :is-loading="status === LoadingStatuses.LOADING"
          :init-value="query"
          :has-autofocus="true"
          @submit-input="(e) => query = e"
          @enter-submit="goToSearchPage"
        />
      </div>
      <div
        v-if="query.length > 0 && results"
        class="search-results-number mt-16 md:px-24 px-16"
      >
        <div>
          {{ results[0].nbHits + results[1].nbHits }}
          {{ $lang('search', 'hits') }}
        </div>
      </div>
      <div
        v-if="
          results &&
            results[0].nbHits + results[1].nbHits > 0 &&
            query.length > 0
        "
        class="pt-24 overflow-y-auto h-full md:px-24 px-16 pb-32"
      >
        <div>
          <div v-for="result in results" :key="result.id">
            <div v-if="result.index === productIndex" class="mb-38">
              <div v-if="result.hits.length > 0">
                <ProductSearchItem
                  v-for="(product, i) in result.hits"
                  :key="product.id"
                  :item="product"
                  :query-i-d="result.queryID"
                  :position="i"
                  @click="$emit('onSearchClose')"
                />
              </div>
            </div>

            <div v-if="result.index === contentIndex" class="mb-38">
              <div v-if="result.hits.length > 0">
                <div class="mb-16 text-small-medium">
                  {{ $lang('search', 'other_hits_heading') }}
                </div>
                <div
                  v-for="content in result.hits"
                  :key="content.id"
                  class="product flex mb-16"
                >
                  <nuxt-link
                    :to="content.permalink"
                    class="text-primary underline text-small-medium"
                  >
                    {{ content.title }}
                  </nuxt-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- No Results Section -->
      <div
        v-if="
          results &&
            results[0].nbHits + results[1].nbHits === 0 &&
            query.length > 0
        "
        class="pt-24 md:px-24 px-16 text-center"
      >
        <div class="text-small mb-24">
          {{ $lang('search', 'no_results_text') || `No results found for "${query}"` }}
        </div>
      </div>
      
      <div v-if="popularSearches" class="mt-24 md:px-24 px-16">
        <div class="text-small-medium">
          {{ $lang('search', 'popular_hits_heading') }}
        </div>
        <div v-if="popularSearches.words.length > 0" class="mt-16">
          <div
            v-for="word in popularSearches.words"
            :key="'popular-searches-' + word"
          >
            <div
              class="
                text-small-medium text-primary
                cursor-pointer
                underline
                mb-12
                text-sm
                capitalize
              "
              @click="setSearchClick(word)"
            >
              {{ word }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </SideMenu>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import SideMenu from '~/components/navigation/SideMenu.vue';
import TextInput from '~/components/forms/TextInput.vue';
import LoadingStatuses from '~/constants/loading-statuses';
import { useConfigStore } from '~/store/config';
import ProductSearchItem from '~/components/search/ProductSearchItem.vue';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';
const router = useRouter();
const config = useConfigStore();
const { algoliaMultipleQueries } = useAlgoliaSearch();
const { sellerCookieOptions } = useCookieConfig();
const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;

const emit = defineEmits<{
  (e: 'onSearchClose'): void;
}>();

const status = ref('');
const query = ref('');
const results = ref<any>(null);

const sideMenu = ref<typeof SideMenu | null>(null);
onMounted(()=> {
  if (sideMenu.value) {
    sideMenu.value.openOverlay();
  }
});

const searchResultsRoute = computed(()=> {
  const routeData = config.pageByTemplate('template-search-results');
  if (routeData) {
    return routeData.route;
  } else {
    return '';
  }
});

const goToSearchPage = () => {
  // Delay submit to enable query param to be set.
  setTimeout(() => {
    emit('onSearchClose');
    router.push(`${searchResultsRoute.value}?s=${query.value}`);
  }, 250);
};

const setSearchClick = (value: string) => {
  emit('onSearchClose');
  router.push(`${searchResultsRoute.value}?s=${value}`);
};

const productIndex = computed(()=> {
  return config.algoliaBaseIndex + '_products';
});
const contentIndex = computed(()=> {
  return config.algoliaBaseIndex + '_content';
});

const popularSearches = computed(()=> {
  return {
    words: config.popularSearches,
  };
});

let productFilter = 'available:true AND NOT var_archived_value:"1"';
if (isSeller.value) {
  productFilter += ' AND brandName:"Ullmax"';
} 

const search = async(value: string) => {
  console.log('search: ' + value);
  const queries = [
    {
      indexName: productIndex.value,
      query: value,
      params: {
        hitsPerPage: 8,
        filters: productFilter,
        clickAnalytics: true, // Enable queryID for tracking
      },
    },
    {
      indexName: contentIndex.value,
      query: value,
      params: {
        hitsPerPage: 8,
      },
    },
  ];

  const res = await algoliaMultipleQueries(queries);
  if (res) {
    console.log(res);
    results.value = res.results;
  } else {
    console.error();
  }

};

watch(query, (newVal) => {
  search(newVal);
});

</script>
